// The block's own layout, as a file you can open, send, or hand to an
// assistant.
//
// The two boxes in the editor are halves of one thing, and everything an
// author wants to do with them afterwards wants them together: opening the
// layout in a browser to check it, mailing it to a colleague, pasting it into
// an assistant and asking for a change. So this is one HTML document with the
// CSS in a `<style>` block rather than two files that have to be kept side by
// side — and because the block folds a pasted `<style>` back into its CSS box
// (see extractStyleBlocks in customCss.js), the same file pastes straight back
// into the editor and lands in both boxes again.
//
// What is added on top of the author's own markup is scaffolding to make the
// file render on its own at the slide's proportions. It is fenced with plain
// comments saying so, because the next reader of this file may well be a
// language model being asked to change the layout, and it needs to be able to
// tell the layout from the frame around it.
import { SLIDE_GEOMETRY } from './customHtmlBrief';
import { buildCustomHtmlImageVars } from './customCss';

export const CUSTOM_HTML_LAYOUT_FILENAME = 'lessonplanner-custom-slide-layout.html';

/**
 * The comments that fence the author's own two halves inside the exported
 * file, so a reader — a person, an assistant, or the importer in
 * customHtmlImport.js — can tell the layout from the scaffolding wrapped
 * around it to make the file render on its own.
 */
export const LAYOUT_FILE_MARKERS = {
  cssStart: 'BLOCK CSS — this is exactly what is in the editor’s CSS box',
  cssEnd: 'end BLOCK CSS',
  htmlStart: 'BLOCK HTML — this is exactly what is in the editor’s HTML box',
  htmlEnd: 'end BLOCK HTML',
  frameStart: 'PREVIEW FRAME — not part of the block. It only makes this file',
  frameEnd: 'end PREVIEW FRAME'
};

/** A filename fragment from a slide or deck name; empty when there is none. */
const slug = (value) => (value || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 48);

export const customHtmlLayoutFilename = ({ slideName = '', deckName = '' } = {}) => {
  const name = slug(slideName) || slug(deckName);
  return name ? `lessonplanner-layout-${name}.html` : CUSTOM_HTML_LAYOUT_FILENAME;
};

/** Nothing to export when both boxes are empty. */
export const hasCustomHtmlContent = ({ html = '', css = '' } = {}) =>
  Boolean((html || '').trim() || (css || '').trim());

const escapeHtml = (value) => (value || '')
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

// A name goes into the file's header comment, and two things must not survive
// the trip: `--`, which is not allowed inside a comment and whose `-->` form
// would end it early, and angle brackets, which would then be live markup
// rather than a name. Neutralised rather than dropped, so the header still
// reads as the name the author gave.
const commentSafe = (value) => (value || '')
  .replace(/[<>]/g, '')
  .replace(/--+/g, '–');

const indent = (text, pad = '    ') => (text || '')
  .split('\n')
  .map((line) => (line.trim() ? `${pad}${line}` : line))
  .join('\n');

/**
 * The whole block as one HTML document.
 *
 * `images` and `videos` are the block's own libraries: the picture variables
 * are written out so a layout that refers to a slot by name still shows the
 * picture when the file is opened on its own, and linked clips are listed in
 * the header because their markup is a poster and a link rather than anything
 * the file could play.
 */
export const buildCustomHtmlLayoutFile = ({
  html = '',
  css = '',
  blockId = '',
  slideName = '',
  deckName = '',
  deckScoped = false,
  images = [],
  videos = [],
  exportedAt = new Date()
} = {}) => {
  const markup = (html || '').trim();
  const styles = (css || '').trim();
  const title = [slideName, deckName].filter(Boolean).join(' — ')
    || 'Custom HTML/CSS layout';
  const stamp = exportedAt instanceof Date && !Number.isNaN(exportedAt.valueOf())
    ? exportedAt.toISOString().slice(0, 10)
    : '';

  const imageVars = buildCustomHtmlImageVars({ id: blockId, images });
  const videoNote = (videos || []).length
    ? `\n  Linked clips: ${videos.map((video) => video.name || video.url || 'clip').join(', ')}.\n  A clip is a poster picture and a link in the exported deck, so this file\n  shows it the same way rather than playing it.`
    : '';

  const header = [
    '  Custom HTML/CSS slide layout — exported from LessonPlanner One',
    stamp ? `  Exported: ${stamp}` : '',
    slideName ? `  Slide: ${commentSafe(slideName)}` : '',
    deckName ? `  Deck: ${commentSafe(deckName)}` : '',
    '',
    '  This one file holds both halves of the block:',
    `    • the CSS, in the <style> block, marked "${LAYOUT_FILE_MARKERS.cssStart.split('—')[0].trim()}"`,
    `    • the HTML, in the <body>, marked "${LAYOUT_FILE_MARKERS.htmlStart.split('—')[0].trim()}"`,
    '',
    '  To put it back: paste the whole file into the block’s HTML box. The block',
    '  folds a pasted <style> tag into its CSS box, so both halves land where',
    '  they came from.',
    '',
    '  To change it with an assistant: send this file and say what you want',
    '  different. Ask for the two marked regions back, and keep the rules the',
    '  block enforces — the Examples & brief tab in the editor downloads them.',
    deckScoped
      ? '\n  This block’s CSS is deck-scoped: its selectors are left global and reach\n  every slide in the deck. Keep that in mind when renaming classes.'
      : '\n  This block’s CSS is slide-scoped: in the deck every selector is prefixed\n  with the block, so the rules cannot reach another slide. Here it is\n  unprefixed, which is what the editor’s CSS box holds.',
    videoNote
  ].filter((line) => line !== '').join('\n');

  const frame = `html, body {
      margin: 0;
      background: #eef2f7;
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif;
    }
    .lp-stage {
      width: min(100%, ${SLIDE_GEOMETRY.contentWidthPx}px);
      /* The slide's content region — the slide minus its header bar. */
      aspect-ratio: ${SLIDE_GEOMETRY.contentAspect};
      margin: 24px auto;
      overflow: hidden;
      background: #ffffff;
      box-shadow: 0 10px 30px rgba(15, 23, 42, 0.18);
    }
    /* The element the block is rendered into, copied from the app so cqw units
       and em sizes resolve here exactly as they do on the slide. */
    .slide-custom-html {
      width: 100%;
      min-width: 0;
      container-type: inline-size;
      font-size: 16px;
      line-height: 1.5;
    }`;

  return `<!doctype html>
<!--
${header}
-->
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <title>${escapeHtml(title)}</title>
  <style>
    /* ${LAYOUT_FILE_MARKERS.frameStart}
       stand on its own at the slide's proportions. Delete it and nothing about
       the layout changes. */
${indent(frame, '    ')}
    /* ${LAYOUT_FILE_MARKERS.frameEnd} */
${imageVars ? `
    /* Pictures this layout links by slot name, so they show here too. */
${indent(imageVars, '    ')}
` : ''}
    /* ${LAYOUT_FILE_MARKERS.cssStart} */
${indent(styles || '/* (the CSS box is empty) */', '    ')}
    /* ${LAYOUT_FILE_MARKERS.cssEnd} */
  </style>
</head>
<body>
  <div class="lp-stage">
    <div class="slide-custom-html">
      <!-- ${LAYOUT_FILE_MARKERS.htmlStart} -->
${indent(markup || '<!-- (the HTML box is empty) -->', '      ')}
      <!-- ${LAYOUT_FILE_MARKERS.htmlEnd} -->
    </div>
  </div>
</body>
</html>
`;
};
